import React, { useEffect, useState } from 'react';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import { authAPI, tripsAPI } from '../api/api';
import ViewTripModal from '../modal/ViewTripModal';
import '../css/RouteSheet.css';
import { FaChevronDown, FaChevronUp, FaCar, FaRoute, FaCalendarAlt, FaEdit, FaTimes, FaSave, FaFileDownload, FaFileExcel, FaFilePdf, FaEye } from 'react-icons/fa';

const RouteSheet = ({ isSidebarClosed, setIsSidebarClosed }) => {
  const [user, setUser] = useState(null);
  const [vehicleTrips, setVehicleTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [expandedVehicles, setExpandedVehicles] = useState({});
  const [expandedDays, setExpandedDays] = useState({});
  const [editingTrip, setEditingTrip] = useState(null);
  const [editForm, setEditForm] = useState({
    trip_type: '',
    start_city: '',
    start_street: '',
    end_city: '',
    end_street: '',
    description: ''
  });
  const [saving, setSaving] = useState(false);
  const [selectedTrips, setSelectedTrips] = useState({});
  const [odometerValues, setOdometerValues] = useState({}); // { "vehicleId-day-start": value, "vehicleId-day-end": value }
  const [viewingTrip, setViewingTrip] = useState(null);

  useEffect(() => {
    const currentUser = authAPI.getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
      fetchTrips(currentUser.id, selectedYear, selectedMonth);
    }
  }, [selectedYear, selectedMonth]);

  const fetchTrips = async (userId, year, month) => {
    try {
      setLoading(true);
      const result = await tripsAPI.getUserTrips(userId, year, month);
      setVehicleTrips(result.data || []);

      // Auto expand if only one vehicle
      if (result.data && result.data.length === 1) {
        setExpandedVehicles({ [result.data[0].vehicle.id]: true });
      }
    } catch (error) {
      console.error('Error fetching trips:', error);
      setVehicleTrips([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleVehicleExpand = (vehicleId) => {
    setExpandedVehicles(prev => ({
      ...prev,
      [vehicleId]: !prev[vehicleId]
    }));
  };

  const toggleDayExpand = (vehicleId, day) => {
    const key = `${vehicleId}-${day}`;
    setExpandedDays(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const groupTripsByDay = (trips) => {
    const grouped = {};
    trips.forEach(trip => {
      if (!trip.start_time) return;
      const date = new Date(trip.start_time);
      const day = date.toLocaleDateString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit' });
      if (!grouped[day]) {
        grouped[day] = [];
      }
      grouped[day].push(trip);
    });
    // Sort days in descending order
    return Object.keys(grouped)
      .sort((a, b) => new Date(b.split('. ').reverse().join('-')) - new Date(a.split('. ').reverse().join('-')))
      .reduce((acc, day) => {
        acc[day] = grouped[day];
        return acc;
      }, {});
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('hu-HU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDistance = (distance) => {
    if (!distance) return '-';
    return `${parseFloat(distance).toFixed(2)} km`;
  };

  const formatLocation = (address, latitude, longitude) => {
    if (!latitude || !longitude) return '-';

    const coords = `${parseFloat(latitude).toFixed(6)}, ${parseFloat(longitude).toFixed(6)}`;

    if (address && address !== coords) {
      return (
        <div className="location-display">
          <div className="location-address">{address}</div>
          <div className="location-coords">{coords}</div>
        </div>
      );
    }

    return coords;
  };

  const handleEditTrip = (trip) => {
    setEditingTrip(trip);
    setEditForm({
      trip_type: trip.trip_type,
      start_city: '',
      start_street: '',
      end_city: '',
      end_street: '',
      description: trip.description || ''
    });
  };

  // const handleViewTrip = (trip) => {
  //   setViewingTrip(true);
  // };

  const handleCloseEdit = () => {
    setEditingTrip(null);
    setEditForm({
      trip_type: '',
      start_city: '',
      start_street: '',
      end_city: '',
      end_street: '',
      description: ''
    });
  };

  const handleSaveTrip = async () => {
    if (!editingTrip) return;

    const dataToSend = {
      trip_id: editingTrip.id,
      trip_type: editForm.trip_type,
      start_city: editForm.start_city,
      start_street: editForm.start_street,
      end_city: editForm.end_city,
      end_street: editForm.end_street,
      description: editForm.description
    };

    try {
      setSaving(true);
      await tripsAPI.updateTrip(dataToSend);

      // Refresh trips data
      await fetchTrips(user.id, selectedYear, selectedMonth);
      handleCloseEdit();
    } catch (error) {
      console.error('Error saving trip:', error);
      alert('Hiba történt az út mentése során: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleTripSelection = (vehicleId, tripId, tripType, distance) => {
    // Don't allow selection of private trips or 0 km trips
    if (tripType === 'private' || parseFloat(distance || 0) === 0) return;

    setSelectedTrips(prev => {
      const key = `${vehicleId}-${tripId}`;
      const newSelected = { ...prev };
      if (newSelected[key]) {
        delete newSelected[key];
      } else {
        newSelected[key] = true;
      }
      return newSelected;
    });
  };

  const toggleAllTripsForVehicle = (vehicleId, trips) => {
    setSelectedTrips(prev => {
      const newSelected = { ...prev };
      // Only consider business trips with distance > 0 for "select all"
      const selectableTrips = trips.filter(trip => trip.trip_type === 'business' && parseFloat(trip.distance || 0) > 0);
      const allSelectableSelected = selectableTrips.every(trip => prev[`${vehicleId}-${trip.id}`]);

      if (allSelectableSelected) {
        // Deselect all selectable trips
        selectableTrips.forEach(trip => {
          delete newSelected[`${vehicleId}-${trip.id}`];
        });
      } else {
        // Select all selectable trips only
        selectableTrips.forEach(trip => {
          newSelected[`${vehicleId}-${trip.id}`] = true;
        });
      }
      return newSelected;
    });
  };

  const getSelectedTripsData = () => {
    const selectedData = [];
    vehicleTrips.forEach(vehicleData => {
      vehicleData.trips.forEach(trip => {
        if (selectedTrips[`${vehicleData.vehicle.id}-${trip.id}`]) {
          selectedData.push({
            vehicle: vehicleData.vehicle,
            trip: trip
          });
        }
      });
    });
    return selectedData;
  };

  const generateNAVRouteSheet = async (format = 'excel') => {
    const selectedData = getSelectedTripsData();

    if (selectedData.length === 0) {
      alert('Kérlek válassz ki legalább egy utat a menetlevél generálásához!');
      return;
    }

    // Group trips by vehicle
    const tripsByVehicle = {};
    selectedData.forEach(({ vehicle, trip }) => {
      if (!tripsByVehicle[vehicle.id]) {
        tripsByVehicle[vehicle.id] = {
          vehicle,
          trips: []
        };
      }
      tripsByVehicle[vehicle.id].trips.push(trip);
    });

    // Generate route sheet for each vehicle in selected format
    for (const { vehicle, trips } of Object.values(tripsByVehicle)) {
      if (format === 'pdf') {
        await generateVehicleRouteSheetPDF(vehicle, trips);
      } else {
        await generateVehicleRouteSheetExcel(vehicle, trips);
      }
    }
  };

  const generateVehicleRouteSheetExcel = async (vehicle, trips) => {
    try {
      // Sort trips by start time
      const sortedTrips = [...trips].sort((a, b) =>
        new Date(a.start_time) - new Date(b.start_time)
      );

      // Prepare data for backend
      const tripIds = sortedTrips.map(trip => trip.id);

      // Collect odometer values for each day - calculate from trip-level data
      const odometerData = {};

      // Get ALL trips for this vehicle from vehicleTrips state (not just selected ones)
      const vehicleData = vehicleTrips.find(v => v.vehicle.id === vehicle.id);
      const allVehicleTrips = vehicleData?.trips || [];

      // Group ALL trips by day (not just selected)
      const allTripsByDay = {};
      allVehicleTrips.forEach(trip => {
        if (!trip.start_time) return;
        const date = new Date(trip.start_time);
        const day = date.toLocaleDateString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit' });
        if (!allTripsByDay[day]) allTripsByDay[day] = [];
        allTripsByDay[day].push(trip);
      });

      // Calculate odometer data for each day using trip-level values
      Object.keys(allTripsByDay).forEach(day => {
        // Minden selectable trip a napból (nem csak a bepipált)
        const daySelectableTrips = allTripsByDay[day].filter(t => t.trip_type === 'business' && parseFloat(t.distance || 0) > 0);
        if (daySelectableTrips.length === 0) return;

        // Rendezzük időrendbe
        const sortedDayTrips = [...daySelectableTrips].sort((a, b) =>
          new Date(a.start_time) - new Date(b.start_time)
        );

        // Van-e egyáltalán bepipált trip ezen a napon?
        const hasSelectedTrip = sortedDayTrips.some(t => selectedTrips[`${vehicle.id}-${t.id}`]);
        if (!hasSelectedTrip) return;

        // Az időrendben ELSŐ selectable trip-ből vesszük a start_odometer-t (akkor is ha nincs bepipálva)
        const firstTrip = sortedDayTrips[0];
        const firstTripKey = `${vehicle.id}-${firstTrip.id}`;
        const manualStart = parseFloat(odometerValues[firstTripKey]) || 0;
        const obdStart = parseFloat(firstTrip.start_odometer) || 0;
        const startValue = manualStart > 0 ? manualStart : obdStart;

        if (startValue > 0) {
          // Számoljuk ki a végső km-et: indulás + csak a BEPIPÁLT trip-ek távolsága
          let runningOdometer = startValue;
          sortedDayTrips.forEach(t => {
            if (selectedTrips[`${vehicle.id}-${t.id}`]) {
              runningOdometer += parseFloat(t.distance || 0);
            }
          });

          odometerData[day] = {
            start: startValue,
            end: parseFloat(runningOdometer.toFixed(2))
          };
        }
      });

      const requestData = {
        vehicle_id: vehicle.id,
        vehicle_plate: vehicle.plate_number || vehicle.chassis_number17,
        vehicle_brand: vehicle.brand,
        vehicle_model: vehicle.model,
        year: selectedYear,
        month: selectedMonth,
        trip_ids: tripIds,
        user_name: user?.name || '',
        odometer_data: odometerData
      };

      // Call backend API
      const blob = await tripsAPI.generateRouteSheetExcel(requestData);

      // Download the file
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const fileName = `menetlevel_${vehicle.plate_number || vehicle.id}_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.xlsx`;

      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating route sheet:', error);
      alert('Hiba történt a menetlevél generálása során: ' + error.message);
    }
  };

  const generateVehicleRouteSheetPDF = async (vehicle, trips) => {
    try {
      // Sort trips by start time
      const sortedTrips = [...trips].sort((a, b) =>
        new Date(a.start_time) - new Date(b.start_time)
      );

      // Prepare data for backend
      const tripIds = sortedTrips.map(trip => trip.id);

      // Collect odometer values for each day - calculate from trip-level data
      const odometerData = {};

      // Get ALL trips for this vehicle from vehicleTrips state (not just selected ones)
      const vehicleData = vehicleTrips.find(v => v.vehicle.id === vehicle.id);
      const allVehicleTrips = vehicleData?.trips || [];

      // Group ALL trips by day (not just selected)
      const allTripsByDay = {};
      allVehicleTrips.forEach(trip => {
        if (!trip.start_time) return;
        const date = new Date(trip.start_time);
        const day = date.toLocaleDateString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit' });
        if (!allTripsByDay[day]) allTripsByDay[day] = [];
        allTripsByDay[day].push(trip);
      });

      // Calculate odometer data for each day using trip-level values
      Object.keys(allTripsByDay).forEach(day => {
        // Minden selectable trip a napból (nem csak a bepipált)
        const daySelectableTrips = allTripsByDay[day].filter(t => t.trip_type === 'business' && parseFloat(t.distance || 0) > 0);
        if (daySelectableTrips.length === 0) return;

        // Rendezzük időrendbe
        const sortedDayTrips = [...daySelectableTrips].sort((a, b) =>
          new Date(a.start_time) - new Date(b.start_time)
        );

        // Van-e egyáltalán bepipált trip ezen a napon?
        const hasSelectedTrip = sortedDayTrips.some(t => selectedTrips[`${vehicle.id}-${t.id}`]);
        if (!hasSelectedTrip) return;

        // Az időrendben ELSŐ selectable trip-ből vesszük a start_odometer-t (akkor is ha nincs bepipálva)
        const firstTrip = sortedDayTrips[0];
        const firstTripKey = `${vehicle.id}-${firstTrip.id}`;
        const manualStart = parseFloat(odometerValues[firstTripKey]) || 0;
        const obdStart = parseFloat(firstTrip.start_odometer) || 0;
        const startValue = manualStart > 0 ? manualStart : obdStart;

        if (startValue > 0) {
          // Számoljuk ki a végső km-et: indulás + csak a BEPIPÁLT trip-ek távolsága
          let runningOdometer = startValue;
          sortedDayTrips.forEach(t => {
            if (selectedTrips[`${vehicle.id}-${t.id}`]) {
              runningOdometer += parseFloat(t.distance || 0);
            }
          });

          odometerData[day] = {
            start: startValue,
            end: parseFloat(runningOdometer.toFixed(2))
          };
        }
      });

      const requestData = {
        vehicle_id: vehicle.id,
        vehicle_plate: vehicle.plate_number || vehicle.chassis_number17,
        vehicle_brand: vehicle.brand,
        vehicle_model: vehicle.model,
        year: selectedYear,
        month: selectedMonth,
        trip_ids: tripIds,
        user_name: user?.name || '',
        odometer_data: odometerData
      };

      // Call backend API for PDF
      const blob = await tripsAPI.generateRouteSheetPDF(requestData);

      // Download the file
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const fileName = `menetlevel_${vehicle.plate_number || vehicle.id}_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.pdf`;

      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF route sheet:', error);
      alert('Hiba történt a PDF menetlevél generálása során: ' + error.message);
    }
  };

  const monthNames = [
    'Január', 'Február', 'Március', 'Április', 'Május', 'Június',
    'Július', 'Augusztus', 'Szeptember', 'Október', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <div>
      <Navbar isSidebarClosed={isSidebarClosed} setIsSidebarClosed={setIsSidebarClosed} />
      <Sidebar isCollapsed={isSidebarClosed} setIsCollapsed={setIsSidebarClosed} />
      <div className="page-content">
        <div className="routesheet-header">
          <h2 className="routesheet-title">
            <FaRoute style={{ marginRight: '10px' }} />
            Menetlevél kiállítás
          </h2>
          <div className="routesheet-filters">
            <div className="filter-group">
              <label htmlFor="year-select">
                <FaCalendarAlt style={{ marginRight: '5px' }} />
                Év:
              </label>
              <select
                id="year-select"
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="routesheet-select"
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="month-select">Hónap:</label>
              <select
                id="month-select"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="routesheet-select"
              >
                {monthNames.map((name, idx) => (
                  <option key={idx + 1} value={idx + 1}>{name}</option>
                ))}
              </select>
            </div>
            {Object.keys(selectedTrips).length > 0 && (
              <div className="dropdown-wrapper">
                <button
                  className="btn-generate-nav dropdown-btn"
                  onClick={(e) => {
                    e.currentTarget.parentElement.classList.toggle('active');
                  }}
                >
                  <FaFileDownload style={{ marginRight: '8px' }} />
                  Menetlevél generálása ({Object.keys(selectedTrips).length})
                  <span className="dropdown-arrow">▼</span>
                </button>
                <div className="dropdown-menu">
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      generateNAVRouteSheet('excel');
                      document.querySelector('.dropdown-wrapper').classList.remove('active');
                    }}
                  >
                    <FaFileExcel style={{ marginRight: '8px' }} />
                    Excel (.xlsx)
                  </button>
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      generateNAVRouteSheet('pdf');
                      document.querySelector('.dropdown-wrapper').classList.remove('active');
                    }}
                  >
                    <FaFilePdf style={{ marginRight: '8px' }} />
                    PDF (.pdf)
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="routesheet-content">
          {loading ? (
            <div className="routesheet-empty">
              <div className="loading-spinner"></div>
              Menetlevelek betöltése...
            </div>
          ) : vehicleTrips.length === 0 ? (
            <div className="routesheet-empty">
              Nincsenek utak ebben a hónapban.
            </div>
          ) : (
            <div className="vehicles-list">
              {vehicleTrips.map((vehicleData) => (
                <div key={vehicleData.vehicle.id} className="vehicle-card">
                  <div
                    className="vehicle-card-header"
                    onClick={() => toggleVehicleExpand(vehicleData.vehicle.id)}
                  >
                    <div className="vehicle-info">
                      <FaCar className="vehicle-icon" />
                      <div className="vehicle-details">
                        <h3 className="vehicle-name">
                          {vehicleData.vehicle.brand} {vehicleData.vehicle.model}
                        </h3>
                        <div className="vehicle-meta">
                          <span className="vehicle-plate">
                            <strong>Rendszám:</strong> {vehicleData.vehicle.plate_number || '-'}
                          </span>
                          <span className="vehicle-vin">
                            <strong>VIN:</strong> {vehicleData.vehicle.chassis_number17}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="vehicle-stats">
                      <div className="stat">
                        <span className="stat-label">Összesen:</span>
                        <span className="stat-value">{vehicleData.total_trips} út</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Teljes távolság:</span>
                        <span className="stat-value">{formatDistance(vehicleData.total_distance)}</span>
                      </div>
                      <button className="expand-btn">
                        {expandedVehicles[vehicleData.vehicle.id] ? <FaChevronUp /> : <FaChevronDown />}
                      </button>
                    </div>
                  </div>

                  {expandedVehicles[vehicleData.vehicle.id] && (
                    <div className="vehicle-trips">
                      <div className="vehicle-trips-actions">
    
                      </div>

                      {Object.entries(groupTripsByDay(vehicleData.trips)).map(([day, dayTrips]) => {
                        const dayKey = `${vehicleData.vehicle.id}-${day}`;
                        const totalDayDistance = dayTrips.reduce((sum, trip) => sum + parseFloat(trip.distance || 0), 0);

                        return (
                          <div key={dayKey} className="day-group">
                            <div
                              className="day-header"
                              onClick={() => toggleDayExpand(vehicleData.vehicle.id, day)}
                            >
                              <div className="day-info">
                                <FaCalendarAlt style={{ marginRight: '8px' }} />
                                <span className="day-date">{day}</span>
                                <span className="day-trip-count">({dayTrips.length} út)</span>
                                <span className="day-distance">{formatDistance(totalDayDistance)}</span>
                              </div>
                              <button className="expand-btn">
                                {expandedDays[dayKey] ? <FaChevronUp /> : <FaChevronDown />}
                              </button>
                            </div>

                            {expandedDays[dayKey] && (
                              <table className="trips-table">
                                <thead>
                                  <tr>
                                    <th className="checkbox-column">
                                      <input
                                        type="checkbox"
                                        checked={dayTrips.filter(t => t.trip_type === 'business' && parseFloat(t.distance || 0) > 0).length > 0 && dayTrips.filter(t => t.trip_type === 'business' && parseFloat(t.distance || 0) > 0).every(trip => selectedTrips[`${vehicleData.vehicle.id}-${trip.id}`])}
                                        onChange={() => {
                                          const selectableTrips = dayTrips.filter(t => t.trip_type === 'business' && parseFloat(t.distance || 0) > 0);
                                          const allSelectableSelected = selectableTrips.every(trip => selectedTrips[`${vehicleData.vehicle.id}-${trip.id}`]);
                                          selectableTrips.forEach(trip => {
                                            const key = `${vehicleData.vehicle.id}-${trip.id}`;
                                            setSelectedTrips(prev => {
                                              const newSelected = { ...prev };
                                              if (allSelectableSelected) {
                                                delete newSelected[key];
                                              } else {
                                                newSelected[key] = true;
                                              }
                                              return newSelected;
                                            });
                                          });
                                        }}
                                        title="Összes napi üzleti út kijelölése/törlése (0 km nélkül)"
                                      />
                                    </th>
                                    <th>Indulás</th>
                                    <th>Érkezés</th>
                                    <th>Kezdőpont</th>
                                    <th>Végpont</th>
                                    <th>Típus</th>
                                    <th>Távolság</th>
                                    <th>Km óra állás</th>
                                    <th>Műveletek</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(() => {
                                    const selectableTrips = dayTrips.filter(t => t.trip_type === 'business' && parseFloat(t.distance || 0) > 0);

                                    // Rendezzük időrendbe a selectable trip-eket
                                    const sortedSelectableTrips = [...selectableTrips].sort((a, b) =>
                                      new Date(a.start_time) - new Date(b.start_time)
                                    );

                                    // Számoljuk ki minden trip indulási és érkezési km-ét
                                    // Az időrendben ELSŐ bepipált trip-nél keressük az OBD/manuális értéket
                                    const tripOdometers = {};
                                    let runningOdometer = null;

                                    // Először megkeressük az időrendben első bepipált trip start_odometer-ét
                                    const firstSelectedTrip = sortedSelectableTrips.find(t =>
                                      selectedTrips[`${vehicleData.vehicle.id}-${t.id}`]
                                    );

                                    if (firstSelectedTrip) {
                                      const firstKey = `${vehicleData.vehicle.id}-${firstSelectedTrip.id}`;
                                      const manualValue = parseFloat(odometerValues[firstKey]) || 0;
                                      const obdValue = parseFloat(firstSelectedTrip.start_odometer) || 0;
                                      runningOdometer = manualValue > 0 ? manualValue : obdValue;
                                    }

                                    // Ha nincs bepipált trip, az első selectable trip-ből vesszük
                                    if (runningOdometer === null && sortedSelectableTrips.length > 0) {
                                      const firstTrip = sortedSelectableTrips[0];
                                      const firstKey = `${vehicleData.vehicle.id}-${firstTrip.id}`;
                                      const manualValue = parseFloat(odometerValues[firstKey]) || 0;
                                      const obdValue = parseFloat(firstTrip.start_odometer) || 0;
                                      runningOdometer = manualValue > 0 ? manualValue : obdValue;
                                    }

                                    // Számoljuk ki az odometer értékeket időrendben
                                    sortedSelectableTrips.forEach((t) => {
                                      const odometerKey = `${vehicleData.vehicle.id}-${t.id}`;

                                      // Ha van manuális felülírás ennél a trip-nél, azt használjuk
                                      const manualValue = parseFloat(odometerValues[odometerKey]) || 0;
                                      if (manualValue > 0) {
                                        runningOdometer = manualValue;
                                      }

                                      tripOdometers[t.id] = {
                                        start: runningOdometer,
                                        end: runningOdometer > 0 ? runningOdometer + parseFloat(t.distance || 0) : 0
                                      };

                                      // Következő trip indulási km-e = ez a trip érkezési km-e
                                      runningOdometer = tripOdometers[t.id].end;
                                    });

                                    // Az időrendben legelső selectable trip ID-ja
                                    const firstSelectableTripId = sortedSelectableTrips.length > 0 ? sortedSelectableTrips[0].id : null;

                                    return dayTrips.map((trip) => {
                                      const isSelectable = trip.trip_type === 'business' && parseFloat(trip.distance || 0) > 0;
                                      const tripOdometer = tripOdometers[trip.id];
                                      const odometerKey = `${vehicleData.vehicle.id}-${trip.id}`;

                                      // Csak az első selectable trip-nél jelenjen meg az input
                                      const isFirstSelectable = trip.id === firstSelectableTripId;

                                      // Van-e OBD-ből jövő érték ehhez a trip-hez?
                                      const hasObdValue = parseFloat(trip.start_odometer) > 0;

                                      // Megjelenítendő indulási érték (manuális > OBD > számított)
                                      const displayStartOdometer = odometerValues[odometerKey] !== undefined
                                        ? odometerValues[odometerKey]
                                        : (hasObdValue ? trip.start_odometer : (tripOdometer?.start > 0 ? tripOdometer.start.toFixed(2) : ''));

                                      return (
                                        <tr key={trip.id} className={selectedTrips[`${vehicleData.vehicle.id}-${trip.id}`] ? 'selected-trip' : ''}>
                                          <td data-label="Kiválasztás" className="checkbox-column">
                                            <input
                                              type="checkbox"
                                              checked={!!selectedTrips[`${vehicleData.vehicle.id}-${trip.id}`]}
                                              onChange={() => toggleTripSelection(vehicleData.vehicle.id, trip.id, trip.trip_type, trip.distance)}
                                              onClick={(e) => e.stopPropagation()}
                                              disabled={trip.trip_type === 'private' || parseFloat(trip.distance || 0) === 0}
                                              title={trip.trip_type === 'private' ? 'Magán utak nem kerülhetnek menetlevélbe' : parseFloat(trip.distance || 0) === 0 ? '0 km-es utak nem kerülhetnek menetlevélbe' : 'Kiválasztás menetlevélhez'}
                                            />
                                          </td>
                                          <td data-label="Indulás">{formatDate(trip.start_time)}</td>
                                          <td data-label="Érkezés">{formatDate(trip.end_time)}</td>
                                          <td data-label="Kezdőpont">
                                            {formatLocation(trip.start_address, trip.start_latitude, trip.start_longitude)}
                                          </td>
                                          <td data-label="Végpont">
                                            {formatLocation(trip.end_address, trip.end_latitude, trip.end_longitude)}
                                          </td>
                                          <td data-label="Típus">
                                            <span className={`trip-type-badge ${trip.trip_type}`}>
                                              {trip.trip_type_label}
                                            </span>
                                          </td>
                                          <td data-label="Távolság">{formatDistance(trip.distance)}</td>
                                          <td data-label="Km óra állás" className="odometer-column">
                                            {isFirstSelectable && (
                                              <div className="odometer-input-wrapper">
                                                <label>Indulás:</label>
                                                <input
                                                  type="number"
                                                  className={`odometer-input ${hasObdValue ? 'odometer-obd' : ''}`}
                                                  placeholder="km"
                                                  value={displayStartOdometer}
                                                  onChange={(e) => setOdometerValues(prev => ({ ...prev, [odometerKey]: e.target.value }))}
                                                  onClick={(e) => e.stopPropagation()}
                                                  title={hasObdValue ? 'OBD-ből kiolvasott érték (szerkeszthető)' : 'Indulási km óra állás'}
                                                />
                                              </div>
                                            )}
                                          </td>
                                          <td data-label="Műveletek">
                                            <button
                                              className="view-trip-btn"
                                              onClick={() => setViewingTrip(trip)}
                                              title="Út megtekintése"
                                            >
                                              <FaEye />
                                            </button>
                                            <button
                                              className="edit-trip-btn"
                                              onClick={() => handleEditTrip(trip)}
                                              title="Út szerkesztése"
                                            >
                                              <FaEdit />
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    });
                                  })()}
                                </tbody>
                              </table>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Edit Trip Modal */}
        {editingTrip && (
          <div className="modal-overlay" onClick={handleCloseEdit}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Út szerkesztése</h3>
                <button className="modal-close-btn" onClick={handleCloseEdit}>
                  <FaTimes />
                </button>
              </div>
              <div className="modal-body">
                {editingTrip && (
                  <div className="current-addresses">
                    <div className="address-info">
                      <strong>Jelenlegi kiindulópont:</strong> {editingTrip.start_address || 'Nincs cím'}
                    </div>
                    <div className="address-info">
                      <strong>Jelenlegi célpont:</strong> {editingTrip.end_address || 'Nincs cím'}
                    </div>
                  </div>
                )}

                <div className="form-section">
                  <h4>Út típusa</h4>
                  <div className="form-group">
                    <select
                      id="trip-type"
                      value={editForm.trip_type}
                      onChange={(e) => setEditForm({ ...editForm, trip_type: e.target.value })}
                      className="form-control"
                    >
                      <option value="business">Üzleti</option>
                      <option value="private">Magán</option>
                    </select>
                  </div>
                </div>

                <div className="form-section">
                  <h4>Új kiindulópont címe</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="start-city">Város</label>
                      <input
                        id="start-city"
                        type="text"
                        value={editForm.start_city}
                        onChange={(e) => setEditForm({ ...editForm, start_city: e.target.value })}
                        className="form-control"
                        placeholder="Budapest"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="start-street">Utca (házszámmal)</label>
                      <input
                        id="start-street"
                        type="text"
                        value={editForm.start_street}
                        onChange={(e) => setEditForm({ ...editForm, start_street: e.target.value })}
                        className="form-control"
                        placeholder="Fő utca 12"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h4>Új célpont címe</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="end-city">Város</label>
                      <input
                        id="end-city"
                        type="text"
                        value={editForm.end_city}
                        onChange={(e) => setEditForm({ ...editForm, end_city: e.target.value })}
                        className="form-control"
                        placeholder="Budapest"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="end-street">Utca (házszámmal)</label>
                      <input
                        id="end-street"
                        type="text"
                        value={editForm.end_street}
                        onChange={(e) => setEditForm({ ...editForm, end_street: e.target.value })}
                        className="form-control"
                        placeholder="Fő utca 12"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h4>Leírás</h4>
                  <div className="form-group">
                    <textarea
                      id="description"
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      className="form-control"
                      placeholder="Út leírása (opcionális)"
                      rows={3}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={handleCloseEdit} disabled={saving}>
                  Mégse
                </button>
                <button className="btn btn-primary" onClick={handleSaveTrip} disabled={saving}>
                  <FaSave style={{ marginRight: '8px' }} />
                  {saving ? 'Mentés...' : 'Mentés'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Trip Modal */}
        <ViewTripModal
          open={!!viewingTrip}
          onClose={() => setViewingTrip(null)}
          trip={viewingTrip}
        />
      </div>
    </div>
  );
};

export default RouteSheet;
